import {
  Army,
  ArmyList,
  ArmyListSize,
  ArmyListUnit,
  ArmyListUnitOption,
  ArmyOrganisation,
  ArmyOrganisationGroup,
  MagicItem,
  MagicItemCategory,
  MagicStandard,
  ModelRule,
  ModelRuleLimit,
  Organisation,
  Unit,
  UnitOption,
  UserFavoriteUnit,
} from '@Models';
import { AppState } from '@Redux/store';
import { ArmyListUnitState } from '@Stores/index';

export interface ArmyListUnitProps {
  armyListUnit: ArmyListUnit;
}

export const getArmyList = (state: AppState): ArmyList | null =>
  state.armyList.entity ?? null;

export const getArmyListState = (state: AppState): Boolean =>
  state.armyList.isLoading;

export const getArmyListUnits = (state: AppState): ArmyListUnit[] =>
  state.armyList.entity?.army_list_units ?? [];

export const getActiveArmyListUnits = (state: AppState): ArmyListUnit[] =>
  state.armyList.entity?.army_list_units?.filter(
    (item) => !item.isDeleted && item.is_enabled,
  ) ?? [];

export const getArmyListUnitState = (state: AppState): ArmyListUnitState =>
  state.armyListUnit;

export const getArmyListUnit = (state: AppState): ArmyListUnit | null =>
  state.armyListUnit.entity ?? null;

export const getCurrentArmyListUnit = (
  state: AppState,
  props: ArmyListUnitProps | null,
): ArmyListUnit | null => props?.armyListUnit ?? getArmyListUnit(state);

export const getArmyListUnitSize = (state: AppState): number =>
  getArmyListUnit(state)?.size ?? 0;

export const getCurrentArmyListUnitSize = (
  state: AppState,
  props: ArmyListUnitProps | null,
): number => props?.armyListUnit?.size ?? getArmyListUnitSize(state);

export const getArmyListUnitOptions = (state: AppState): ArmyListUnitOption[] =>
  getArmyListUnit(state)?.army_list_unit_unit_options?.filter(
    (item) => !item.isDeleted,
  ) ?? [];

export const getCurrentArmyListUnitOptions = (
  state: AppState,
  props: ArmyListUnitProps | null,
): ArmyListUnitOption[] =>
  props?.armyListUnit?.army_list_unit_unit_options?.filter(
    (item) => !item.isDeleted,
  ) ?? getArmyListUnitOptions(state);

export const getArmyListSize = (state: AppState): ArmyListSize =>
  state.armyList.entity?.size ?? ArmyListSize.Standard;

export const getArmy = (state: AppState): Army | null =>
  state.army.entity ?? null;

export const getArmyOrganisation = (state: AppState): ArmyOrganisation | null =>
  getArmy(state)?.army_organisations?.find(
    (item) => item.id === getArmyList(state)?.army_organisation_id,
  ) ?? null;

export const getArmyOrganisationGroups = (
  state: AppState,
): ArmyOrganisationGroup[] =>
  getArmyOrganisation(state)?.army_organisation_groups ?? [];

export const getUnits = (state: AppState): Unit[] =>
  getArmy(state)?.units ?? [];

export const getSelectableUnits = (state: AppState): Unit[] =>
  getUnits(state)
    .filter((item) => !item.is_mount && !item.is_desabled)
    .filter((item) => item.points && item.points != 0) ?? [];

export const getUnit = (state: AppState): Unit | null =>
  getUnits(state).find(
    (unit) => unit.id === state.armyListUnit.entity?.unit_id,
  ) ?? null;

export const getCurrentUnit = (
  state: AppState,
  props: ArmyListUnitProps | null,
): Unit | null =>
  getUnits(state).find((unit) => unit.id === props?.armyListUnit?.unit_id) ??
  getUnit(state);

export const getUnitOptions = (state: AppState): UnitOption[] =>
  getUnits(state)
    .find((unit) => unit.id === state.armyListUnit.entity?.unit_id)
    ?.optionGroups?.flatMap((o) => o.options) ?? [];

export const getCurrentUnitOptions = (
  state: AppState,
  props: ArmyListUnitProps | null,
): Array<UnitOption> =>
  getUnits(state)
    .find(
      (unit) =>
        unit.id ===
        (props?.armyListUnit?.unit_id || state.armyListUnit.entity?.unit_id),
    )
    ?.optionGroups?.flatMap((o) => o.options) ?? [];

export const getMagicItems = (state: AppState): MagicItem[] =>
  getArmy(state)?.magic_items ?? [];

export const getMagicStandards = (state: AppState): MagicStandard[] =>
  getArmy(state)?.magic_standards ?? [];

export const getMagicItemsWithOutModelRuleCategory = (
  state: AppState,
): MagicItem[] =>
  getMagicItems(state).filter(
    (item) => item.modelRuleCategories.length === 0,
  ) ?? [];

export const getMagicItemsWithModelRuleCategory = (
  state: AppState,
): MagicItem[] =>
  getMagicItems(state).filter((item) => item.modelRuleCategories.length != 0) ??
  [];

export const getModelRules = (state: AppState): ModelRule[] =>
  getArmy(state)?.modelRules ?? [];

export const getModelRuleLimits = (state: AppState): ModelRuleLimit[] =>
  getArmy(state)?.modelRuleLimits ?? [];

export const getMagicItemCategories = (state: AppState): MagicItemCategory[] =>
  getArmy(state)?.magic_item_categories ?? [];

export const getOrganisations = (state: AppState): Organisation[] =>
  getArmy(state)?.organisations ?? [];

export const unique = (value: any, index: any, self: any): boolean => {
  return self.indexOf(value) === index;
};

export const getUserFavoriteUnits = (state: AppState): UserFavoriteUnit[] =>
  state.userFavoriteUnits.entity ?? [];
